import { useState, useEffect } from "react";
import { useQuery, useMutation } from "@apollo/client";
import {
  GET_SELECTED_PRODUCTS_QUERY,
  GET_SELECTED_SPEND_CATEGORIES_QUERY,
  UPDATE_SELECTED_PRODUCTS_MUTATION,
  UPDATE_SELECTED_SPEND_CATEGORIES_MUTATION,
} from "../../../apis/salesforce/queries";
import { HG_FORCE_CLIENT } from "../../../utils/consts";

interface UpdateResult {
  success: boolean;
  error?: string;
}

interface IntegrationSelectionsReturn {
  products: {
    value: number[];
    loading: boolean;
    updating: boolean;
    handleUpdate: (productData: {
      ids: number[];
      inclusionMethod: string;
    }) => Promise<UpdateResult>;
  };
  categories: {
    value: string[];
    loading: boolean;
    updating: boolean;
    handleUpdate: (categoryIds: string[]) => Promise<UpdateResult>;
  };
}

export const useIntegrationSelections = (
  integrationId?: string
): IntegrationSelectionsReturn => {
  // Local optimistic state
  const [localSelectedProducts, setLocalSelectedProducts] = useState<number[]>(
    []
  );
  const [localSelectedSpendCategories, setLocalSelectedSpendCategories] =
    useState<string[]>([]);

  // Query for selected products
  const {
    data: productsData,
    loading: loadingSelectedProducts,
    refetch: refetchServerSelectedProducts,
  } = useQuery<{ selectedProducts: number[] }>(GET_SELECTED_PRODUCTS_QUERY, {
    variables: { integrationId },
    context: { clientName: HG_FORCE_CLIENT },
    skip: !integrationId,
    fetchPolicy: "cache-first",
    errorPolicy: "all",
  });

  // Query for selected spend categories
  const {
    data: categoriesData,
    loading: loadingSelectedSpendCategories,
    refetch: refetchServerSelectedSpendCategories,
  } = useQuery<{ selectedSpendCategories: string[] }>(
    GET_SELECTED_SPEND_CATEGORIES_QUERY,
    {
      variables: { integrationId },
      context: { clientName: HG_FORCE_CLIENT },
      skip: !integrationId,
      fetchPolicy: "cache-first",
      errorPolicy: "all",
    }
  );

  // Mutation for updating products
  const [updateSelectedProductsMutation, { loading: updatingProducts }] =
    useMutation<{ updateSelectedProducts: boolean }>(
      UPDATE_SELECTED_PRODUCTS_MUTATION,
      {
        context: { clientName: HG_FORCE_CLIENT },
        errorPolicy: "all",
      }
    );

  // Mutation for updating spend categories
  const [
    updateSelectedSpendCategoriesMutation,
    { loading: updatingSpendCategories },
  ] = useMutation<{ updateSelectedSpendCategories: boolean }>(
    UPDATE_SELECTED_SPEND_CATEGORIES_MUTATION,
    {
      context: { clientName: HG_FORCE_CLIENT },
      errorPolicy: "all",
    }
  );

  // Initialize local state with server data when loading finishes OR when server data changes
  useEffect(() => {
    const serverSelectedProducts = productsData?.selectedProducts || [];
    if (!loadingSelectedProducts && serverSelectedProducts) {
      setLocalSelectedProducts(serverSelectedProducts);
    }
  }, [loadingSelectedProducts, productsData?.selectedProducts]);

  useEffect(() => {
    const serverSelectedSpendCategories =
      categoriesData?.selectedSpendCategories || [];
    if (!loadingSelectedSpendCategories && serverSelectedSpendCategories) {
      setLocalSelectedSpendCategories(serverSelectedSpendCategories);
    }
  }, [loadingSelectedSpendCategories, categoriesData?.selectedSpendCategories]);

  // Handle product selection
  const handleProductsUpdate = async (productData: {
    ids: number[];
    inclusionMethod: string;
  }): Promise<UpdateResult> => {
    // Update local state immediately for optimistic UI
    setLocalSelectedProducts(productData.ids);

    try {
      const response = await updateSelectedProductsMutation({
        variables: { integrationId, ids: productData.ids },
      });

      // Throw if there are GraphQL errors
      if (response.errors?.length) {
        throw response.errors;
      }

      return { success: true };
    } catch (error) {
      // Refetch server data to stay in sync
      const result = await refetchServerSelectedProducts();

      // Force sync local state with refetched data
      if (result.data?.selectedProducts) {
        setLocalSelectedProducts(result.data.selectedProducts);
      }

      // Check if it's GraphQL errors array and get first error
      if (Array.isArray(error) && error[0]) {
        return {
          success: false,
          error: error?.[0]?.message,
        };
      }

      // Network or other errors
      return { success: false, error: "generic_error" };
    }
  };

  // Handle spend category selection
  const handleCategoriesUpdate = async (
    categoryIds: string[]
  ): Promise<UpdateResult> => {
    // Update local state immediately for optimistic UI
    setLocalSelectedSpendCategories(categoryIds);

    try {
      const response = await updateSelectedSpendCategoriesMutation({
        variables: { integrationId, ids: categoryIds },
      });

      // Throw if there are GraphQL errors
      if (response.errors?.length) {
        throw response.errors;
      }

      return { success: true };
    } catch {
      // Refetch server data to stay in sync
      const result = await refetchServerSelectedSpendCategories();
      // Force sync local state with refetched data
      if (result.data?.selectedSpendCategories) {
        setLocalSelectedSpendCategories(result.data.selectedSpendCategories);
      }

      return { success: false, error: "generic_error" };
    }
  };

  return {
    products: {
      value: localSelectedProducts,
      loading: loadingSelectedProducts,
      updating: updatingProducts,
      handleUpdate: handleProductsUpdate,
    },
    categories: {
      value: localSelectedSpendCategories,
      loading: loadingSelectedSpendCategories,
      updating: updatingSpendCategories,
      handleUpdate: handleCategoriesUpdate,
    },
  };
};
